import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, User, Building2, GripVertical, Linkedin } from "lucide-react";
import { api, teamPhotoUrl } from "@/lib/api";

const inputCls = "w-full bg-[#141414] border border-[#2a2a2a] px-3.5 py-2.5 text-[13px] text-[#e8e8e8] rounded focus:outline-none focus:border-[#4deb7a] transition-colors";
const labelCls = "block font-mono-dm uppercase tracking-[0.1em] text-[9px] text-[#8f8f8f] mb-1.5";

function MemberModal({ initial, onClose, onSaved }) {
  const blank = { name: "", role: "", description: "", companies: [], compensation: "", equity: "", vesting_period: "", cliff: "", notes: "", linkedin: "", contractual: false, verbal_commitment: false, prospect: true };
  const [form, setForm] = useState({ ...blank, ...(initial || {}), companies: initial?.companies || [] });
  const [companyInput, setCompanyInput] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [preview, setPreview] = useState(initial?.id && initial?.photo_path ? teamPhotoUrl(initial.id) : null);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addCompany = () => {
    const v = companyInput.trim();
    if (v && !form.companies.includes(v)) setForm({ ...form, companies: [...form.companies, v] });
    setCompanyInput("");
  };
  const removeCompany = (c) => setForm({ ...form, companies: form.companies.filter((x) => x !== c) });

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (f) { setPhotoFile(f); setPreview(URL.createObjectURL(f)); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      let id = initial?.id;
      if (id) await api.put(`/admin/team/members/${id}`, form);
      else { const { data } = await api.post(`/admin/team/members`, form); id = data.id; }
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        await api.post(`/admin/team/members/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(initial?.id ? "Member updated" : "Member added");
      onSaved();
    } catch { toast.error("Failed to save member"); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] px-5 py-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#0e0e0e] border border-[#222] rounded w-full max-w-lg p-6 my-auto" onClick={(e) => e.stopPropagation()} data-testid="member-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif-pf text-[24px] text-[#f0f0f0]">{initial?.id ? "Edit" : "New"} member</h3>
          <button onClick={onClose} className="text-[#8f8f8f] hover:text-[#e8e8e8]"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          {/* Photo */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-[#141414] border border-[#2a2a2a] overflow-hidden flex items-center justify-center shrink-0">
              {preview ? <img src={preview} alt="" className="w-full h-full object-cover" /> : <User className="h-6 w-6 text-[#8f8f8f]" strokeWidth={1.5} />}
            </div>
            <label className="cursor-pointer inline-flex items-center gap-2 border border-[#2a2a2a] hover:border-[#4deb7a] text-[#d4d4d4] hover:text-[#4deb7a] text-[12px] px-3.5 py-2 rounded transition-colors" data-testid="member-photo-btn">
              <Upload className="h-3.5 w-3.5" /> Photo
              <input type="file" accept="image/*" className="hidden" onChange={onPhoto} />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className={labelCls}>Name</label><input value={form.name} onChange={upd("name")} className={inputCls} data-testid="member-name" /></div>
            <div><label className={labelCls}>Role</label><input value={form.role} onChange={upd("role")} className={inputCls} data-testid="member-role" /></div>
          </div>
          <div><label className={labelCls}>Description</label><textarea rows={2} value={form.description} onChange={upd("description")} className={inputCls} /></div>
          {/* Companies */}
          <div>
            <label className={labelCls}>Companies</label>
            <div className="flex gap-2">
              <input value={companyInput} onChange={(e) => setCompanyInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCompany(); } }} placeholder="Type a company, press Enter" className={inputCls} data-testid="member-company-input" />
              <button type="button" onClick={addCompany} className="border border-[#2a2a2a] text-[#d4d4d4] px-3 rounded text-[12px] hover:border-[#4deb7a]">Add</button>
            </div>
            {form.companies.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.companies.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 bg-[#141414] border border-[#2a2a2a] text-[#d4d4d4] text-[11px] px-2 py-1 rounded">
                    {c} <button type="button" onClick={() => removeCompany(c)} className="text-[#8f8f8f] hover:text-[#c83232]"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div><label className={labelCls}>LinkedIn</label><input value={form.linkedin} onChange={upd("linkedin")} placeholder="https://linkedin.com/in/…" className={inputCls} data-testid="member-linkedin" /></div>
          <div><label className={labelCls}>Compensation</label><input value={form.compensation} onChange={upd("compensation")} placeholder="$120K" className={inputCls} /></div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className={labelCls}>Equity</label><input value={form.equity} onChange={upd("equity")} placeholder="2.5%" className={inputCls} data-testid="member-equity" /></div>
            <div><label className={labelCls}>Vesting period</label><input value={form.vesting_period} onChange={upd("vesting_period")} placeholder="4 years" className={inputCls} /></div>
            <div><label className={labelCls}>Cliff</label><input value={form.cliff} onChange={upd("cliff")} placeholder="1 year" className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Notes</label><textarea rows={2} value={form.notes} onChange={upd("notes")} className={inputCls} /></div>
          <div className="flex flex-wrap gap-x-6 gap-y-2.5">
            <label className="flex items-center gap-2.5 text-[13px] text-[#d4d4d4] cursor-pointer">
              <input type="checkbox" checked={!!form.prospect} onChange={(e) => setForm({ ...form, prospect: e.target.checked })} className="w-4 h-4 accent-[#4deb7a]" data-testid="member-prospect" />
              Prospect
            </label>
            <label className="flex items-center gap-2.5 text-[13px] text-[#d4d4d4] cursor-pointer">
              <input type="checkbox" checked={!!form.contractual} onChange={(e) => setForm({ ...form, contractual: e.target.checked })} className="w-4 h-4 accent-[#4deb7a]" data-testid="member-contractual" />
              Contractual
            </label>
            <label className="flex items-center gap-2.5 text-[13px] text-[#d4d4d4] cursor-pointer">
              <input type="checkbox" checked={!!form.verbal_commitment} onChange={(e) => setForm({ ...form, verbal_commitment: e.target.checked })} className="w-4 h-4 accent-[#4deb7a]" data-testid="member-verbal-commitment" />
              Verbal Commitment
            </label>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="submit" className="bg-[#4deb7a] text-[#080808] font-medium text-[13px] px-5 py-2.5 rounded" data-testid="member-save">{initial?.id ? "Save changes" : "Add member"}</button>
            <button type="button" onClick={onClose} className="text-[13px] text-[#bdbdbd] px-3">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TeamSection({ section, isAdmin, onChanged }) {
  const [modal, setModal] = useState(null);
  const [members, setMembers] = useState(section.team_members || []);
  const [dragId, setDragId] = useState(null);
  const [commitFilter, setCommitFilter] = useState("all");
  useEffect(() => { setMembers(section.team_members || []); }, [section.team_members]);

  const del = async (id) => {
    if (!window.confirm("Delete this member?")) return;
    await api.delete(`/admin/team/members/${id}`);
    toast.success("Member removed");
    onChanged?.();
  };

  const handleDrop = async (targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const arr = [...members];
    const from = arr.findIndex((m) => m.id === dragId);
    const to = arr.findIndex((m) => m.id === targetId);
    if (from === -1 || to === -1) { setDragId(null); return; }
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setMembers(arr);
    setDragId(null);
    try { await api.put(`/admin/team/members/reorder`, { order: arr.map((m) => m.id) }); onChanged?.(); }
    catch { toast.error("Failed to save order"); setMembers(section.team_members || []); }
  };

  return (
    <div data-testid="team-section">
      {(isAdmin || members.length > 0) && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          {isAdmin && <button data-testid="create-member-btn" onClick={() => setModal({ new: true })} className="inline-flex items-center gap-2 bg-[#4deb7a] hover:bg-[#3dd869] text-[#080808] text-[12px] font-medium px-4 py-2.5 rounded transition-colors">
            <Plus className="h-4 w-4" strokeWidth={1.5} /> Create member
          </button>}
          {members.length > 0 && (() => {
            const nContract = members.filter((m) => m.contractual).length;
            const nVerbal = members.filter((m) => m.verbal_commitment).length;
            const nProspect = members.filter((m) => m.prospect).length;
            const opts = [
              { id: "all", label: `All (${members.length})` },
              { id: "contractual", label: `Contractual (${nContract})` },
              { id: "verbal", label: `Verbal (${nVerbal})` },
              { id: "prospect", label: `Prospect (${nProspect})` },
            ];
            return (
              <div className="inline-flex items-center rounded-md border border-[#222] overflow-hidden" data-testid="commitment-filter">
                {opts.map((o) => (
                  <button
                    key={o.id}
                    data-testid={`commitment-filter-${o.id}`}
                    onClick={() => setCommitFilter(o.id)}
                    className={`text-[11px] font-mono-dm px-3 py-2 border-l border-[#222] first:border-l-0 transition-colors ${commitFilter === o.id ? "bg-[rgba(77,235,122,0.1)] text-[#4deb7a]" : "text-[#8f8f8f] hover:text-[#e8e8e8] hover:bg-[#141414]"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {members.length === 0 && (
        <div className="bg-[#0e0e0e] border border-[#222] rounded-md py-12 px-8 text-center">
          <User className="h-7 w-7 mx-auto text-[#8f8f8f]" strokeWidth={1.25} />
          <div className="text-[13px] text-[#d4d4d4] font-medium mt-3">No team members yet</div>
          <div className="text-[11px] text-[#8f8f8f]">{isAdmin ? "Add your first member above." : "Team profiles will appear here soon."}</div>
        </div>
      )}

      {(() => {
        const displayed = members.filter((m) => commitFilter === "all" || (commitFilter === "contractual" && m.contractual) || (commitFilter === "verbal" && m.verbal_commitment) || (commitFilter === "prospect" && m.prospect));
        const canDrag = isAdmin && commitFilter === "all";
        if (members.length > 0 && displayed.length === 0) {
          return <div className="bg-[#0e0e0e] border border-[#222] rounded-md py-10 px-8 text-center text-[12px] text-[#8f8f8f]" data-testid="commitment-filter-empty">No members match this commitment filter.</div>;
        }
        return (
          <div className="grid sm:grid-cols-2 gap-3">
            {displayed.map((m) => (
              <div key={m.id}
                draggable={canDrag}
                onDragStart={() => canDrag && setDragId(m.id)}
                onDragOver={(e) => canDrag && e.preventDefault()}
                onDrop={() => canDrag && handleDrop(m.id)}
                className={`group bg-[#0e0e0e] border border-[#222] rounded p-5 hover:border-[#2a2a2a] transition-opacity ${dragId === m.id ? "opacity-40" : ""}`} data-testid={`member-card-${m.id}`}>
                <div className="flex items-start gap-4">
                  {canDrag && <GripVertical data-testid={`member-drag-${m.id}`} className="h-4 w-4 text-[#5a5a5a] cursor-grab shrink-0 mt-0.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />}
                  <div className="w-14 h-14 rounded-full bg-[#141414] border border-[#2a2a2a] overflow-hidden flex items-center justify-center shrink-0">
                    {m.photo_path ? <img src={teamPhotoUrl(m.id)} alt={m.name} className="w-full h-full object-cover" /> : <User className="h-6 w-6 text-[#8f8f8f]" strokeWidth={1.5} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[15px] text-[#f0f0f0] font-medium truncate">{m.name || "Unnamed"}</div>
                    {m.role && <div className="text-[12px] text-[#4deb7a]">{m.role}</div>}
                    {(m.prospect || m.verbal_commitment || m.contractual) && (
                      <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`member-status-${m.id}`}>
                        {m.prospect && <span data-testid={`member-prospect-badge-${m.id}`} className="inline-block bg-[rgba(120,160,220,0.1)] border border-[rgba(120,160,220,0.25)] text-[#8fb0e0] px-2 py-0.5 rounded-sm font-mono-dm text-[9px] uppercase tracking-wider">Prospect</span>}
                        {m.verbal_commitment && <span data-testid={`member-verbal-badge-${m.id}`} className="inline-block bg-[rgba(220,140,40,0.12)] border border-[rgba(220,140,40,0.25)] text-[#e0a244] px-2 py-0.5 rounded-sm font-mono-dm text-[9px] uppercase tracking-wider">Verbal Commitment</span>}
                        {m.contractual && <span data-testid={`member-contractual-badge-${m.id}`} className="inline-block bg-[rgba(77,235,122,0.08)] border border-[rgba(77,235,122,0.2)] text-[#4deb7a] px-2 py-0.5 rounded-sm font-mono-dm text-[9px] uppercase tracking-wider">Contractual</span>}
                      </div>
                    )}
                    {m.linkedin && <a href={m.linkedin} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`member-linkedin-link-${m.id}`} className="inline-flex items-center gap-1 text-[11px] text-[#8f8f8f] hover:text-[#4deb7a] transition-colors mt-1"><Linkedin className="h-3 w-3" strokeWidth={1.5} /> LinkedIn</a>}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity shrink-0">
                      <button data-testid={`member-edit-${m.id}`} onClick={() => setModal({ member: m })} className="text-[#8f8f8f] hover:text-[#e8e8e8]"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`member-delete-${m.id}`} onClick={() => del(m.id)} className="text-[#8f8f8f] hover:text-[#c83232]"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                </div>
                {m.description && <div className="text-[12px] text-[#d4d4d4] leading-snug mt-2">{m.description}</div>}
                {m.companies?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {m.companies.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 bg-[#141414] border border-[#222] text-[#bdbdbd] text-[10px] px-2 py-0.5 rounded"><Building2 className="h-2.5 w-2.5" /> {c}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Admin-only comp detail */}
            {isAdmin && (m.equity || m.compensation || m.vesting_period || m.cliff || m.notes) && (
              <div className="mt-4 pt-3 border-t border-[#1a1a1a] space-y-2">
                {(m.equity || m.vesting_period || m.cliff) && (
                  <div className="grid grid-cols-3 gap-x-4">
                    <div className="text-[11px]"><span className="text-[#8f8f8f]">Equity: </span><span className="text-[#4deb7a] font-mono-dm">{m.equity || "—"}</span></div>
                    <div className="text-[11px]"><span className="text-[#8f8f8f]">Vesting: </span><span className="text-[#e8e8e8]">{m.vesting_period || "—"}</span></div>
                    <div className="text-[11px]"><span className="text-[#8f8f8f]">Cliff: </span><span className="text-[#e8e8e8]">{m.cliff || "—"}</span></div>
                  </div>
                )}
                {m.compensation && <div className="text-[11px]"><span className="text-[#8f8f8f]">Comp: </span><span className="text-[#e8e8e8]">{m.compensation}</span></div>}
                {m.notes && <div className="text-[11px] text-[#bdbdbd] leading-snug">{m.notes}</div>}
              </div>
            )}
          </div>
            ))}
          </div>
        );
      })()}

      {modal && <MemberModal initial={modal.member} onClose={() => setModal(null)} onSaved={() => { setModal(null); onChanged?.(); }} />}
    </div>
  );
}
